import { useRef, useCallback, useEffect } from "react";
import { Platform } from "react-native";
import type { GridStore } from "../components/Grids/grid-store";

interface DragSelectionConfig {
  onSelectionChange: (selections: Array<{ day: number | string; hour: number }>) => void;
  getGridState: () => Record<string, boolean>;
  store: GridStore;
}

interface CellInfo {
  day: number | string;
  hour: number;
  element: HTMLElement;
}

/**
 * Web-specific drag selection hook using mouse events.
 * Allows users to click and drag to select/deselect multiple cells.
 * Distinguishes between clicks (single cell toggle) and drags (multi-cell selection).
 *
 * All drag state (isDragging, draggedCells) lives in the Zustand store so
 * individual cells can subscribe to their own drag status without causing
 * the entire grid to re-render.
 */
export function useWebDragSelection({ onSelectionChange, getGridState, store }: DragSelectionConfig) {
  const cellRefs = useRef<{ [key: string]: CellInfo }>({});
  const isSelectingRef = useRef<boolean | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track mouse position to distinguish clicks from drags
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  const hasMovedRef = useRef<boolean>(false);
  const initialCellRef = useRef<string | null>(null);

  // Stable refs for callbacks used inside event handler closures
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const getGridStateRef = useRef(getGridState);
  getGridStateRef.current = getGridState;

  // Ref to hold cleanup function for global listeners
  const cleanupRef = useRef<(() => void) | null>(null);

  const registerCell = useCallback((day: number | string, hour: number, element: HTMLElement | null) => {
    const key = `${day}-${hour}`;
    if (element) {
      cellRefs.current[key] = { day, hour, element };
    } else {
      delete cellRefs.current[key];
    }
  }, []);

  const getCellAtPosition = useCallback(
    (clientX: number, clientY: number): CellInfo | null => {
      const { slotsPerDay } = store.getState();
      // Find the cell element at the given position — skip hidden cells
      for (const [, cellInfo] of Object.entries(cellRefs.current)) {
        if (cellInfo.hour >= slotsPerDay) continue;
        const rect = cellInfo.element.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
          return cellInfo;
        }
      }
      return null;
    },
    [store],
  );

  const handleCellSelection = useCallback(
    (day: number | string, hour: number) => {
      const key = `${day}-${hour}`;

      // Initialize selection mode on first cell
      if (isSelectingRef.current === null) {
        // Determine if we're selecting or deselecting based on the first cell's current state
        isSelectingRef.current = !getGridStateRef.current()[key];
      }

      // Add to dragged cells in the store — only the affected cell re-renders
      store.getState().addDraggedCell(key);
    },
    [store],
  );

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      // Only handle left mouse button
      if (event.button !== 0) return;

      // Only start tracking if the click landed on a grid cell.
      // Clicks on other elements inside the container (e.g. granularity
      // selector) should not trigger drag state changes.
      const cellInfo = getCellAtPosition(event.clientX, event.clientY);
      if (!cellInfo) return;

      // Reset tracking refs
      mouseDownPosRef.current = { x: event.clientX, y: event.clientY };
      hasMovedRef.current = false;
      isSelectingRef.current = null;
      initialCellRef.current = `${cellInfo.day}-${cellInfo.hour}`;

      // Attach global listeners inline — removed in upHandler.
      // This avoids React state for isMouseDown and the associated re-renders.
      const moveHandler = (e: MouseEvent) => {
        if (mouseDownPosRef.current === null) return;

        // Check if mouse has moved significantly (more than 3px)
        const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
        const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
        const moved = dx > 3 || dy > 3;

        if (moved && !hasMovedRef.current) {
          // Mouse has moved — this is a drag, not a click
          hasMovedRef.current = true;
          e.preventDefault();

          // Set isDragging + clear draggedCells in store atomically
          store.getState().startDrag();

          // Set cursor imperatively (no React re-render)
          document.body.style.cursor = "grabbing";

          // Start drag selection with initial cell
          if (initialCellRef.current) {
            const [d, h] = initialCellRef.current.split("-");
            handleCellSelection(isNaN(Number(d)) ? d : Number(d), Number(h));
          }
        }

        if (hasMovedRef.current) {
          // Continue drag selection
          e.preventDefault();
          const ci = getCellAtPosition(e.clientX, e.clientY);
          if (ci) {
            handleCellSelection(ci.day, ci.hour);
          }
        }
      };

      const upHandler = () => {
        if (mouseDownPosRef.current === null) return;

        const wasDragging = hasMovedRef.current;
        mouseDownPosRef.current = null;
        hasMovedRef.current = false;

        if (wasDragging) {
          // Apply selection changes from dragged cells
          const { draggedCells } = store.getState();
          const selections = Object.keys(draggedCells)
            .filter((k) => draggedCells[k])
            .map((cellKey) => {
              const [d, h] = cellKey.split("-");
              return {
                day: isNaN(Number(d)) ? d : Number(d),
                hour: Number(h),
              };
            });

          if (selections.length > 0) {
            onSelectionChangeRef.current(selections);
          }

          // Clear drag state in store — sets dragEndedAt to block
          // accidental press handlers briefly after drag ends
          store.getState().endDrag();

          // Reset cursor
          document.body.style.cursor = "";
        }
        // If not dragging (was a click), don't touch the store —
        // let the cell's onPress handle the click normally.

        initialCellRef.current = null;
        isSelectingRef.current = null;

        // Remove global listeners
        document.removeEventListener("mousemove", moveHandler);
        document.removeEventListener("mouseup", upHandler);
        cleanupRef.current = null;
      };

      document.addEventListener("mousemove", moveHandler, { passive: false });
      document.addEventListener("mouseup", upHandler);
      cleanupRef.current = () => {
        document.removeEventListener("mousemove", moveHandler);
        document.removeEventListener("mouseup", upHandler);
      };
    },
    [getCellAtPosition, handleCellSelection, store],
  );

  // Clean up global listeners on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const getContainerProps = useCallback((): Record<string, unknown> => {
    if (Platform.OS !== "web") return {};

    return {
      onMouseDown: handleMouseDown,
      style: {
        userSelect: "none",
        WebkitUserSelect: "none",
      },
    };
  }, [handleMouseDown]);

  return {
    registerCell,
    containerRef,
    getContainerProps,
  };
}
